import { ApiError, type MeContext } from "@khazzin/api-client";
import { endSession } from "@khazzin/auth";
import { EmptyState } from "@khazzin/ui/patterns/page";
import { Button } from "@khazzin/ui/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { ME_CONTEXT_KEY, useStoreScope } from "../../app/shell/scope";
import { captureInviteToken, clearInviteToken } from "./team";

/**
 * `/invitations/accept#token=…` (EP10-S10, EP01-S15): the link an owner shared.
 *
 * The token is moved out of the address into this tab's session storage before
 * anything else, so a sign-in round trip does not lose it and the address bar
 * does not keep it. Once signed in it is redeemed exactly once — a second POST
 * would be `410 invitation_used` — and the person lands in the store.
 *
 * A `410 invitation_used` is usually the email match having got there first
 * (EP01-S15 §4): `/me/context` is reloaded, and a person who now belongs to a
 * store is taken in rather than shown an error.
 */
export function AcceptInvitationPage() {
	const { t } = useLocale();
	const { user, isLoading, signIn } = useAuth();
	const api = useApi();
	const queryClient = useQueryClient();
	const { switchStore } = useStoreScope();
	const [token] = useState(captureInviteToken);
	const [outcome, setOutcome] = useState<"working" | "in" | string>("working");
	// StrictMode runs an effect twice; the token must be spent once.
	const started = useRef(false);

	useEffect(() => {
		if (user === null || isLoading || token === null || started.current) {
			return;
		}
		started.current = true;
		void (async () => {
			try {
				const accepted = await api.acceptStoreInvitation(token);
				clearInviteToken();
				await queryClient.invalidateQueries({ queryKey: ME_CONTEXT_KEY });
				switchStore(accepted.member.storeId);
				setOutcome("in");
			} catch (cause) {
				clearInviteToken();
				const code = cause instanceof ApiError ? cause.code : "";
				if (code === "invitation_used") {
					const context = await queryClient.fetchQuery<MeContext>({
						queryKey: ME_CONTEXT_KEY,
						queryFn: ({ signal }) => api.getMeContext({ signal }),
						staleTime: 0,
					});
					if (context.stores.length > 0) {
						setOutcome("in");
						return;
					}
				}
				setOutcome(code === "" ? "failed" : code);
			}
		})();
	}, [user, isLoading, token, api, queryClient, switchStore]);

	if (outcome === "in") {
		return <Navigate to="/" replace />;
	}
	if (token === null) {
		return (
			<Screen
				title={t("portal.invite.missingTitle")}
				body={t("portal.invite.missing")}
				action={<WayOut />}
			/>
		);
	}
	if (!isLoading && user === null) {
		return (
			<Screen
				title={t("portal.invite.title")}
				body={t("portal.invite.signIn")}
				action={
					<Button variant="primary" className="min-h-12" onClick={() => void signIn()}>
						{t("portal.shell.signIn")}
					</Button>
				}
			/>
		);
	}
	if (outcome !== "working") {
		const known = ["invitation_used", "invitation_expired", "invitation_revoked", "not_found"];
		return (
			<Screen
				title={t("portal.invite.failedTitle")}
				body={t(`portal.invite.${known.includes(outcome) ? outcome : "failed"}`)}
				action={<WayOut />}
			/>
		);
	}
	return <Screen title={t("portal.invite.title")} body={t("portal.invite.working")} />;
}

/**
 * Somewhere to go from a link that did not work (ADJ-M03-4): the portal —
 * which lands on the person's store if they have one — or, signed in with
 * the wrong address, Sign out to try another.
 */
function WayOut() {
	const { t } = useLocale();
	const { user, signOut } = useAuth();
	const api = useApi();
	const [leaving, setLeaving] = useState(false);
	return (
		<div className="flex flex-wrap justify-center gap-2">
			<Link
				to="/"
				className="inline-flex min-h-12 items-center rounded-md border border-brand bg-brand px-4 text-[13px] font-bold text-fg-on-brand hover:bg-brand-hover"
			>
				{t("portal.invite.toPortal")}
			</Link>
			{user !== null && (
				<Button
					variant="secondary"
					className="min-h-12"
					loading={leaving}
					onClick={() => {
						setLeaving(true);
						void endSession(api, signOut).finally(() => setLeaving(false));
					}}
				>
					{t("portal.shell.signOut")}
				</Button>
			)}
		</div>
	);
}

function Screen({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
	return (
		<main className="grid min-h-screen place-items-center bg-bg-canvas px-5">
			<EmptyState title={title} body={body} {...(action === undefined ? {} : { action })} />
		</main>
	);
}
